import { PaginationSetting } from 'constants/paginationSetting';
import { useCallback, useContext, useState } from 'react';
import { Client, Context as UrqlContext } from 'urql';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { getFiles, startDownload } from 'views/utils/download';
import {
  GetIssuanceDocumentFileListQueryVariables,
  IssuanceDocumentFileInfo,
  useGetIssuanceDocumentFileListQuery,
} from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';

export type IssuanceDocumentFileListProps = {
  folderId: string;
  defaultKeyWord: string;
};

export const useIssuanceDocumentFileList = ({ folderId, defaultKeyWord }: IssuanceDocumentFileListProps) => {
  const { showError, showWarn } = useGlobalToastHandler();
  const [searchKeyWord, setSearchKeyWord] = useState<string>(defaultKeyWord);
  const [selectedItems, setSelectedItems] = useState<Array<IssuanceDocumentFileInfo>>([]);
  const [queryVariables, setQueryVariables] = useState<GetIssuanceDocumentFileListQueryVariables>({
    id: folderId,
    keyword: defaultKeyWord || undefined,
    count: PaginationSetting.pageCount,
    page: 1,
  });

  const submitKeyWord = useCallback((keyword: string) => {
    setSearchKeyWord(keyword);
    setQueryVariables((state) => {
      // :型名を経由した方が型チェックされる return { ...state, otherWord:"1111" } としてもエラーにならない
      const variables: GetIssuanceDocumentFileListQueryVariables = {
        ...state,
        keyword,
      };
      return variables;
    });
  }, []);

  const [documentFileList, _] = useGetIssuanceDocumentFileListQuery({
    variables: queryVariables,
    requestPolicy: 'network-only',
  });

  const client = useContext(UrqlContext) as Client;

  const onSelect = useCallback(
    (selectFile: IssuanceDocumentFileInfo) => {
      setSelectedItems((currentItems) => {
        const selected = currentItems.find((x) => x.ID === selectFile.ID);
        if (selected) {
          return currentItems.filter((x) => x !== selected);
        } else {
          return [...currentItems, selectFile];
        }
      });
    },
    [setSelectedItems],
  );

  const download = useOnceSubmit<IssuanceDocumentFileInfo>(
    {
      onSubmit: async (fileInfo: IssuanceDocumentFileInfo) => {
        try {
          const files = await getFiles(client, [{ id: fileInfo.ID, fileName: fileInfo.DisplayName }]);
          await startDownload(files);
        } catch (error) {
          showError('ファイルのダウンロードに失敗しました');
        }
      },
    },
    [client, selectedItems, showError],
  );

  const handleMultiDownload = useOnceSubmit<void>(
    {
      onPreviousSubmit: async () => {
        if (selectedItems.length === 0) {
          showWarn('ダウンロードしたいファイルを選択してください');
          return false;
        }

        return true;
      },
      onSubmit: async () => {
        try {
          const files = await getFiles(
            client,
            selectedItems.map((x) => ({ id: x.ID, fileName: x.DisplayName })),
          );
          await startDownload(files);
        } catch (error) {
          showError('ファイルのダウンロードに失敗しました');
        }
      },
    },
    [client, selectedItems, showError],
  );

  return {
    documentFileList,
    searchKeyWord,
    setSearchKeyWord,
    submitKeyWord,
    currentPage: queryVariables.page,
    setCurrentPage: (page: number) => {
      setQueryVariables((state) => ({
        ...state,
        page,
      }));
    },
    perPageCount: PaginationSetting.pageCount,
    selectedItems,
    onSelect,
    download,
    handleMultiDownload,
  };
};
